import React from 'react';
import {useMutation} from '@tanstack/react-query';
import {useForm} from 'react-hook-form';
import {Link} from 'react-router-dom';
import {apiClient} from '@common/http/query-client';
import {useAuth} from '@common/auth/use-auth';
import {FileUploadProvider} from '@common/uploads/uploader/file-upload-provider';
import {FormFileEntryField} from '@common/uploads/components/file-entry-field';
import {Form} from '@ui/forms/form';
import {FormSelect} from '@ui/forms/select/select';
import {Item} from '@ui/forms/listbox/item';
import {FormTextField} from '@ui/forms/input-field/text-field/text-field';
import {Button} from '@ui/buttons/button';
import {toast} from '@ui/toast/toast';
import {Trans} from '@ui/i18n/trans';
import {BusinessIcon} from '@common/icons/material/Business';
import {CloudUploadIcon} from '@common/icons/material/CloudUpload';
import {CheckCircleIcon} from '@common/icons/material/CheckCircle';

type CompanyType = 'label' | 'aggregator' | 'distributor';

interface PartnerRegistrationForm {
  company_name: string;
  trade_name?: string;
  company_type: CompanyType;
  tax_id?: string;
  country?: string;
  address?: {
    street?: string;
    number?: string;
    city?: string;
    region?: string;
    postal_code?: string;
    country?: string;
  };
  responsible_name: string;
  corporate_email: string;
  phone?: string;
  website?: string;
  approximate_artists?: number;
  approximate_catalog?: number;
  social_links?: {
    instagram?: string;
    youtube?: string;
    spotify?: string;
    linkedin?: string;
  };
  validation_document_url?: string;
  contract_url?: string;
  operation_description?: string;
  future_bank_info?: {
    bank_name?: string;
    account_holder?: string;
    notes?: string;
  };
  terms_accepted: boolean;
  username?: string;
  password?: string;
}

export function RegisterPartnerAccountGadPage() {
  const {user} = useAuth();
  const form = useForm<PartnerRegistrationForm>({
    defaultValues: {
      company_type: 'label',
      country: 'Brasil',
      address: {country: 'Brasil'},
      social_links: {},
      future_bank_info: {},
      terms_accepted: false,
    },
  });

  const createAccount = useMutation({
    mutationFn: (payload: PartnerRegistrationForm) =>
      apiClient.post('gad/partner-accounts', payload).then(r => r.data),
    onSuccess: () => {
      toast.positive('Cadastro enviado para análise.');
      form.reset({
        company_type: 'label',
        country: 'Brasil',
        address: {country: 'Brasil'},
        social_links: {},
        future_bank_info: {},
        terms_accepted: false,
      });
    },
  });

  return (
    <FileUploadProvider>
      <main className="min-h-screen bg-alt">
        <div className="mx-auto max-w-1280 px-24 py-32 md:py-48">
          <div className="mb-32 flex flex-wrap items-center justify-between gap-16">
            <div>
              <div className="mb-8 flex items-center gap-10 text-sm font-semibold uppercase text-primary">
                <BusinessIcon size="sm" />
                <span>Ecossistema GAD</span>
              </div>
              <h1 className="text-3xl font-semibold text-main md:text-4xl">
                Cadastro de Gravadoras, Agregadoras e Distribuidoras
              </h1>
              <p className="mt-10 max-w-720 text-sm text-muted md:text-base">
                Envie os dados comerciais, documentais e operacionais para
                aprovação manual antes da liberação das APIs Sandbox e Produção.
              </p>
            </div>
            <Button
              elementType={Link}
              to="/dashboard-partner-gad"
              variant="outline"
              color="primary"
            >
              Ver painel
            </Button>
          </div>

          <Form
            form={form}
            onSubmit={values => createAccount.mutate(values)}
            className="space-y-24"
          >
            <Section title="Empresa">
              <Grid>
                <FormTextField name="company_name" label="Nome da empresa" required />
                <FormTextField name="trade_name" label="Nome fantasia" />
                <FormSelect
                  name="company_type"
                  selectionMode="single"
                  label="Tipo da empresa"
                >
                  <Item value="label">Gravadora</Item>
                  <Item value="aggregator">Agregadora</Item>
                  <Item value="distributor">Distribuidora</Item>
                </FormSelect>
                <FormTextField name="tax_id" label="CNPJ / Tax ID" />
                <FormTextField name="country" label="País" />
                <FormTextField name="website" label="Website" type="url" />
              </Grid>
            </Section>

            <Section title="Endereço e responsável">
              <Grid>
                <FormTextField name="address.street" label="Endereço" />
                <FormTextField name="address.number" label="Número / complemento" />
                <FormTextField name="address.city" label="Cidade" />
                <FormTextField name="address.region" label="Estado / região" />
                <FormTextField name="address.postal_code" label="CEP / código postal" />
                <FormTextField name="address.country" label="País do endereço" />
                <FormTextField name="responsible_name" label="Responsável legal" required />
                <FormTextField name="corporate_email" label="Email corporativo" type="email" required />
                <FormTextField name="phone" label="Telefone" />
              </Grid>
            </Section>

            {!user && (
              <Section title="Acesso">
                <Grid>
                  <FormTextField name="username" label="Usuário" />
                  <FormTextField name="password" label="Senha" type="password" required />
                </Grid>
              </Section>
            )}

            <Section title="Catálogo e operação">
              <Grid>
                <FormTextField
                  name="approximate_artists"
                  label="Quantidade aproximada de artistas"
                  type="number"
                  min={0}
                />
                <FormTextField
                  name="approximate_catalog"
                  label="Quantidade aproximada de catálogo"
                  type="number"
                  min={0}
                />
                <FormTextField name="social_links.instagram" label="Instagram" type="url" />
                <FormTextField name="social_links.youtube" label="YouTube" type="url" />
                <FormTextField name="social_links.spotify" label="Spotify" type="url" />
                <FormTextField name="social_links.linkedin" label="LinkedIn" type="url" />
              </Grid>
              <FormTextField
                name="operation_description"
                label="Descrição da operação"
                inputElementType="textarea"
                rows={5}
                className="mt-24"
              />
            </Section>

            <Section title="Documentos e licenciamento">
              <Grid>
                <FormFileEntryField
                  name="validation_document_url"
                  label="Documento de validação"
                  diskPrefix="gad_partner_documents"
                  allowedFileTypes={['application/pdf', 'image/png', 'image/jpeg']}
                  showRemoveButton
                />
                <FormFileEntryField
                  name="contract_url"
                  label="Contrato/licenciamento"
                  diskPrefix="gad_partner_contracts"
                  allowedFileTypes={['application/pdf', 'image/png', 'image/jpeg']}
                  showRemoveButton
                />
              </Grid>
              <div className="mt-16 flex items-center gap-8 text-sm text-muted">
                <CloudUploadIcon size="sm" />
                <span>Arquivos ficam em storage privado e são revisados pelo admin.</span>
              </div>
            </Section>

            <Section title="Informações bancárias futuras">
              <Grid>
                <FormTextField name="future_bank_info.bank_name" label="Banco" />
                <FormTextField name="future_bank_info.account_holder" label="Titular" />
                <FormTextField
                  name="future_bank_info.notes"
                  label="Observações financeiras"
                  inputElementType="textarea"
                  rows={3}
                />
              </Grid>
            </Section>

            <div className="rounded border bg-paper p-20">
              <label className="flex items-start gap-12 text-sm">
                <input
                  type="checkbox"
                  className="mt-4"
                  {...form.register('terms_accepted', {required: true})}
                />
                <span>
                  Aceito os termos de uso, política de integração, validação
                  documental e regras de responsabilidade sobre metadados,
                  licenciamento, ISRC, UPC/EAN e direitos autorais.
                </span>
              </label>
              {form.formState.errors.terms_accepted && (
                <div className="mt-8 text-sm text-danger">
                  O aceite dos termos é obrigatório.
                </div>
              )}
            </div>

            <div className="flex flex-wrap items-center justify-between gap-16">
              <div className="flex items-center gap-8 text-sm text-muted">
                <CheckCircleIcon size="sm" className="text-positive" />
                <span>Status inicial: Pending Review</span>
              </div>
              <Button
                type="submit"
                variant="flat"
                color="primary"
                disabled={createAccount.isPending}
              >
                <Trans message={createAccount.isPending ? 'Enviando...' : 'Enviar cadastro GAD'} />
              </Button>
            </div>
          </Form>
        </div>
      </main>
    </FileUploadProvider>
  );
}

function Section({title, children}: {title: string; children: React.ReactNode}) {
  return (
    <section className="rounded border bg-paper p-20 md:p-24">
      <h2 className="mb-20 text-xl font-semibold">{title}</h2>
      {children}
    </section>
  );
}

function Grid({children}: {children: React.ReactNode}) {
  return <div className="grid gap-20 md:grid-cols-2 xl:grid-cols-3">{children}</div>;
}
